import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getRandomTopicImage } from '../../../utils/randomTopicImage';

const BLUE = '#47ABDB';

// Same headline treatment as the page's own hero (bold caps + italic
// subtitle, no background box) rather than a separate colored banner.
const SectionHeadline = styled.div`color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:1.35rem;margin-bottom:.2rem;`;
const SectionSubtitle = styled.div`color:#5B5B5B;font-style:italic;font-size:.92rem;margin-bottom:1.1rem;`;

const Row = styled.section`& + &{margin-top:1.3rem;}`;
const GroupLabel = styled.div`color:#003C72;font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:.8rem;margin-bottom:.55rem;`;
// Two cards per row while each can be at least 17rem wide, else one: decided by the width the section
// actually gets (a narrow profile column at 900px squeezed two cards into strips), not by the screen.
const Grid = styled.div`display:grid;gap:1rem;grid-template-columns:repeat(auto-fit,minmax(max(17rem,calc((100% - 1rem) / 2)),1fr));`;
const Card = styled.div`background:#fff;box-shadow:0 6px 16px rgba(15,23,42,.06);padding:1.1rem 1.2rem 1rem;position:relative;display:flex;flex-direction:column;overflow:hidden;`;
// Smaller than before and lifted towards the corner: together with the image's top margin below, the
// ribbon's visible lower edge stays above the image at every card width.
const NewRibbon = styled.div`
  position:absolute;top:0;right:0;background:linear-gradient(to right,${BLUE},#A0C9DA);color:#fff;
  font-size:.58rem;font-weight:800;letter-spacing:.03em;padding:.18rem 1.5rem;
  transform:rotate(45deg) translate(1.1rem,-.55rem);
`;
// One flex row: journal tag, title and authors on the left, the topic image on the right, so the title
// wraps next to the image instead of below a separate top row. A NEW ribbon sits over the card's top
// right corner; when it is there the image starts below it, so the ribbon can never cover the image.
const HeaderRow = styled.div`display:flex;align-items:flex-start;gap:.75rem;`;
const HeaderText = styled.div`min-width:0;flex:1;display:flex;flex-direction:column;align-items:flex-start;`;
const Tag = styled.span`display:inline-block;background:#003C72;color:#fff;font-size:.68rem;font-weight:600;padding:.2rem .55rem;`;
const TopicImage = styled.img`
  width:64px;height:64px;object-fit:contain;border-radius:6px;flex:none;
  margin-top:${(p) => (p.$pushed ? '3rem' : '0')};
  @media(min-width:1180px){width:72px;height:72px;}
`;
const Title = styled.div`
  color:${BLUE};font-weight:800;font-size:1.18rem;line-height:1.32;margin-top:.65rem;
  display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;
`;
const Authors = styled.div`color:#0f4c68;font-size:.8rem;line-height:1.4;margin-top:.4rem;`;
const Spacer = styled.div`flex:1;`;
const SummaryLanguage = styled.div`color:#5B6B78;font-size:.7rem;margin-top:.35rem;`;
const MatchNote = styled.div`color:#0f4c68;font-size:.72rem;font-style:italic;margin-top:.5rem;`;
const ReadButton = styled.a`
  display:flex;align-items:center;justify-content:center;margin-top:.85rem;padding:.6rem 1rem;
  color:#fff;font-weight:700;font-size:.85rem;text-decoration:none;
  background:linear-gradient(to right,${BLUE},#A0C9DA);transition:opacity .2s;
  &:hover{opacity:.9;}
`;
// A quiet secondary control, not Expert's big gradient page button. While loading it is aria-disabled
// rather than disabled: a disabled button loses the keyboard focus, this one keeps it.
const MoreButton = styled.button`
  display:inline-flex;align-items:center;gap:.4rem;margin-top:.75rem;
  color:${BLUE};background:transparent;border:1px solid ${BLUE};
  font-size:.82rem;font-weight:600;padding:.45rem 1rem;cursor:pointer;
  &:hover{background:rgba(71,171,219,.08);}
  &:focus-visible{outline:2px solid ${BLUE};outline-offset:2px;}
  &[aria-disabled='true']{opacity:.55;cursor:default;}
`;
const spin = keyframes`to{transform:rotate(360deg);}`;
const Spinner = styled.span`
  width:.8rem;height:.8rem;border:2px solid rgba(71,171,219,.35);border-top-color:${BLUE};
  border-radius:50%;animation:${spin} .8s linear infinite;flex:none;
`;
const LoadError = styled.div`color:#b42318;font-size:.78rem;margin-top:.35rem;`;

// The studies fetch can take a while - rather than the section just staying
// blank, show an animated ECG trace (an actual heart monitor line, fitting
// for a cardiology-research feed) until it resolves. Same seamless-marquee
// technique as PartnerSection's logo slider: two identical copies of the
// trace sit side by side and slide left by exactly one copy's width, so the
// loop never visibly resets.
const ecgScroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;
const EcgWrap = styled.div`
  position:relative;overflow:hidden;height:64px;border-radius:8px;background:#eef7fb;
  &::after{content:'';position:absolute;inset:0;left:auto;width:2.5rem;background:linear-gradient(to right,transparent,#eef7fb);}
`;
const EcgTrack = styled.div`
  display:flex;width:fit-content;height:100%;align-items:center;
  animation:${ecgScroll} 2.4s linear infinite;
`;
const EcgSvg = styled.svg`
  display:block;flex:none;filter:drop-shadow(0 0 3px rgba(71,171,219,.55));
`;
const LoadingCaption = styled.p`color:#5B6B78;font-size:.82rem;font-style:italic;margin:.6rem 0 0;`;

// One heartbeat "blip" (flat - QRS spike - flat), 70 units wide so several
// can be chained edge to edge into a continuous trace.
const BEAT = [[0, 20], [26, 20], [30, 5], [34, 35], [38, 12], [42, 20], [70, 20]];
const BEATS_PER_COPY = 6;
const ecgPath = Array.from({ length: BEATS_PER_COPY }, (_, i) => i)
  .flatMap((i) => BEAT.filter((_, idx) => i === 0 || idx > 0).map(([x, y]) => [x + i * 70, y]))
  .map(([x, y], idx) => `${idx === 0 ? 'M' : 'L'}${x},${y}`)
  .join(' ');

const EcgTrace = () => (
  <EcgSvg width="280" height="48" viewBox={`0 0 ${BEATS_PER_COPY * 70} 40`} preserveAspectRatio="none">
    <path d={ecgPath} fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </EcgSvg>
);

// The three rows of the HEARTPORT EXPERT section: Austrian research, studies from top journals and
// new studies, two each, chosen on the backend (studies.StudyFeed) by the person's interests, with
// general cardiology filling what the interests did not. Each row can be extended two studies at a
// time; the first-load answer carries hasMore per row, and loadMore(group, offset) resolves to
// {studies, hasMore} of that row. `groups` is null while the answer is pending: the heading shows
// with the ECG trace. A row Expert had nothing for is left out, and the whole section when all three
// are empty.
const GROUPS = [
  { key: 'austrian', id: 'profile.studies.group.austrian', fallback: 'Austrian research' },
  { key: 'top', id: 'profile.studies.group.top', fallback: 'From top journals' },
  { key: 'new', id: 'profile.studies.group.new', fallback: 'New' },
];

const PAGE_SIZE = 2;

// The first two studies of every row are page 0; the next request starts at offset 2.
const initialState = (groups) => ({
  source: groups,
  rows: Object.fromEntries(GROUPS.map(({ key }) => [key, {
    studies: (groups?.[key] || []).slice(),
    hasMore: Boolean(groups?.hasMore?.[key]),
    offset: PAGE_SIZE,
    loading: false,
    failed: false,
  }])),
});

// First two authors, then " et al." when there are more - as Expert's own cards.
const authorsLine = (authors) => {
  if (!Array.isArray(authors) || authors.length === 0) return null;
  return authors.slice(0, 2).join(', ') + (authors.length > 2 ? ' et al.' : '');
};

// subjectName: on a public profile (publicProfile/PublicProfilePage), the person's short name - the
// notes then say why the study is on their page ("Co-authored by …", "… is interested in …").
const RecommendedStudies = ({ t, groups, subjectName, loadMore }) => {
  const { language } = useLanguage();
  const [state, setState] = useState(() => initialState(groups));
  // A new answer (first load, or the same section in the other language) starts from scratch.
  if (state.source !== groups) setState(initialState(groups));

  // The state before this render, for the async handler: it must read the offsets and pmids that are
  // on screen now, not the ones captured when the click started.
  const stateRef = useRef(state);
  stateRef.current = state;

  // One random topic image per pmid, kept while this answer is shown: appending cards must not
  // re-roll the images of cards already on screen, and a new answer re-rolls all of them.
  const images = useRef(new Map());
  const imagesSource = useRef(groups);
  if (imagesSource.current !== groups) {
    images.current = new Map();
    imagesSource.current = groups;
  }
  const imageFor = (pmid) => {
    if (!images.current.has(pmid)) images.current.set(pmid, getRandomTopicImage());
    return images.current.get(pmid);
  };

  const matchNote = (study) => {
    if (study.reason === 'COAUTHOR' && subjectName) {
      return t('profile.studies.coauthored', 'Co-authored by {name}', { name: subjectName });
    }
    if (!study.matchedInterest) return null;
    const interest = t(`speaker.interest.${study.matchedInterest}`, study.matchedInterest);
    return subjectName
      ? t('profile.studies.subjectInterested', '{name} is interested in {interest}', { name: subjectName, interest })
      : t('profile.studies.matchedInterest', 'Because you’re interested in {interest}', { interest });
  };

  const loadMoreFor = async (key) => {
    const current = stateRef.current.rows[key];
    if (!loadMore || !current || !current.hasMore || current.loading) return;
    const source = stateRef.current.source;
    setState((prev) => ({ ...prev, rows: { ...prev.rows, [key]: { ...prev.rows[key], loading: true, failed: false } } }));

    // A pmid already shown anywhere in the section is dropped here; the backend may repeat a study
    // another row shows on a deeper page.
    const shown = new Set();
    Object.values(stateRef.current.rows).forEach((row) => row.studies.forEach((study) => shown.add(study.pmid)));
    let offset = current.offset;
    let hasMore = current.hasMore;
    let added = [];
    let automatic = 0;
    try {
      // Nothing new while there is more? Fetch the next page automatically, at most three times.
      for (;;) {
        const answer = await loadMore(key, offset);
        offset += PAGE_SIZE;
        hasMore = Boolean(answer && answer.hasMore);
        const fresh = ((answer && answer.studies) || []).filter((study) => !shown.has(study.pmid));
        fresh.forEach((study) => shown.add(study.pmid));
        added = added.concat(fresh);
        if (added.length > 0 || !hasMore || automatic >= 3) break;
        automatic += 1;
      }
      setState((prev) => {
        if (prev.source !== source) return prev;
        return { ...prev, rows: { ...prev.rows, [key]: {
          ...prev.rows[key],
          studies: prev.rows[key].studies.concat(added),
          hasMore,
          offset,
          loading: false,
          failed: false,
        } } };
      });
    } catch (error) {
      setState((prev) => {
        if (prev.source !== source) return prev;
        return { ...prev, rows: { ...prev.rows, [key]: { ...prev.rows[key], loading: false, failed: true } } };
      });
    }
  };

  const rows = GROUPS.filter(({ key }) => (state.rows[key].studies || []).length > 0);
  if (groups && rows.length === 0) return null;

  return (
    <div>
      {/* Brand name + tagline, hardcoded like the profile's own hero
          headline - not routed through react-intl. */}
      <SectionHeadline>HEARTPORT EXPERT</SectionHeadline>
      <SectionSubtitle>Latest Research for busy Doctors</SectionSubtitle>
      {!groups ? (
        <>
          <EcgWrap>
            <EcgTrack>
              <EcgTrace />
              <EcgTrace />
            </EcgTrack>
          </EcgWrap>
          <LoadingCaption>{t('profile.studies.loading', 'Reading the latest research…')}</LoadingCaption>
        </>
      ) : rows.map(({ key, id, fallback }) => {
        const row = state.rows[key];
        return (
          <Row key={key} aria-label={t(id, fallback)}>
            <GroupLabel>{t(id, fallback)}</GroupLabel>
            <Grid aria-busy={row.loading}>
              {row.studies.map((study) => {
                const authors = authorsLine(study.authors);
                return (
                  <Card key={study.pmid}>
                    {study.isNew && <NewRibbon>{t('profile.studies.new', 'NEW')}</NewRibbon>}
                    <HeaderRow>
                      <HeaderText>
                        {study.journalName && <Tag>{study.journalName}</Tag>}
                        <Title>{study.title}</Title>
                        {authors && <Authors>{authors}</Authors>}
                      </HeaderText>
                      <TopicImage $pushed={study.isNew} src={imageFor(study.pmid)} alt="" />
                    </HeaderRow>
                    <Spacer />
                    {/* An English page shows the German summary when Expert has no English one. */}
                    {study.textLanguage && study.textLanguage !== language && (
                      <SummaryLanguage>{t('profile.studies.germanSummary', 'German summary')}</SummaryLanguage>
                    )}
                    {matchNote(study) && <MatchNote>{matchNote(study)}</MatchNote>}
                    <ReadButton href={study.url} target="_blank" rel="noreferrer">{t('profile.studies.readStudy', 'Read study')}</ReadButton>
                  </Card>
                );
              })}
            </Grid>
            {loadMore && row.hasMore && (
              <MoreButton type="button" onClick={() => loadMoreFor(key)} aria-disabled={row.loading}>
                {row.loading ? (
                  <>
                    <Spinner aria-hidden="true" />
                    {t('profile.studies.loadingMore', 'Loading…')}
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} aria-hidden="true" />
                    {t('profile.studies.showMore', 'Show more studies')}
                  </>
                )}
              </MoreButton>
            )}
            {row.failed && (
              <LoadError role="status">{t('profile.studies.loadMoreFailed', 'Could not load more studies.')}</LoadError>
            )}
          </Row>
        );
      })}
    </div>
  );
};

export default RecommendedStudies;
